import { z } from 'zod';
import type { CurrencyAmount } from '../money';
import { diffDaysLocal } from '../time';
import { idSchema, localDateSchema, type StatusTone } from './common';

/**
 * Ekran Dziś (MVP §6, Figma 01 Dziś 3018:1898) i baner akceptacji w profilu klienta. Usługi: `today.summary`,
 * `today.pendingApprovals` (packages/core/src/modules/today). Wszystko w zakresie danych osoby: liczby, listy
 * i nazwy pochodzą wyłącznie z rekordów, które osoba widzi.
 *
 * Przełącznik „Moje / Zespół” (`scope`):
 *  - `mine`: zadania, w których jestem wykonawcą (albo bez wykonawcy, dodane przeze mnie), alarmy z mojego Inboxu,
 *    odnowienia klientów, których jestem opiekunem (owner/admin: także klientów bez opiekuna),
 *  - `team`: wszystko w zakresie danych osoby.
 * Akceptacje w obu trybach: te, które osoba może zatwierdzić (flaga wymagana przez typ akcji, zakres klienta).
 */

export const TODAY_SCOPES = ['mine', 'team'] as const;
export type TodayScope = (typeof TODAY_SCOPES)[number];
export const todayScopeSchema = z.enum(TODAY_SCOPES);

export const todaySummaryInput = z.object({ scope: todayScopeSchema.default('mine') }).default({});
export type TodaySummaryInput = z.input<typeof todaySummaryInput>;

/**
 * Pozycja listy „Wymaga Ciebie”. Fala 4: `incident` — otwarty incydent monitoringu (strona nie działa), `message` —
 * wątek poczty do przypisania (nierozpoznany albo niejednoznaczny nadawca); `alarm` = inne „Pilne” bez incydentu
 * (np. pilne zgłoszenie klienta mailem, którego monitoring nie potwierdza). Fala 5: `agent_run` — wynik AI czekający na odbiór
 * (sukces przebiegu bez odbioru; Moje = moje przebiegi, Zespół = zakres osoby, moduł Agenci); wdrożenia czekające na
 * zgodę są akceptacjami (`approval`, podpis „deploy”).
 */
export const TODAY_NEEDS_YOU_KINDS = [
  'incident',
  'alarm',
  'approval',
  'agent_run',
  'message',
  'task',
  'renewal',
] as const;
export type TodayNeedsYouKind = (typeof TODAY_NEEDS_YOU_KINDS)[number];

export interface TodayNeedsYouItem {
  /** `<kind>:<id rekordu>` (stabilny klucz listy) */
  id: string;
  kind: TodayNeedsYouKind;
  title: string;
  tone: StatusTone;
  /** krótki podpis po prawej: „od 09:42”, „email”, „deploy”, „wynik AI czeka”, „do przypisania”, „zablokowane”, „utrzymanie” */
  meta: string;
  /** ścieżka SPA */
  to: string;
  /** pozycja z symulacji (np. incydent symulowanego monitora) — plakietka „Symulacja” */
  simulated?: boolean;
}

/** Pozycja listy „Plan na dziś” (zadania na dziś i po terminie). */
export interface TodayPlanItem {
  id: string;
  number: number;
  title: string;
  /** klient (albo projekt wewnętrzny), np. „REA Nieruchomości”, „Fravity OS” */
  context: string | null;
  /** stan AI („Wynik AI czeka”…), „Po terminie” albo status pracy (W toku, Zablokowane, Do odbioru); null = bez etykiety */
  status: { tone: StatusTone; label: string } | null;
  dueDate: string | null;
  overdue: boolean;
  to: string;
}

export interface TodaySummary {
  scope: TodayScope;
  /** akceptacje, które osoba może zatwierdzić (bez modułu Akceptacje: 0) */
  approvals: { pending: number; oldestWaitingSec: number | null };
  /** otwarte zadania z terminem dziś / po terminie (bez modułu Zadania: 0) */
  tasks: { today: number; overdue: number };
  /**
   * Otwarte alarmy (kompozycja Fali 4): otwarte incydenty monitoringu w zakresie osoby (moduł Utrzymanie; bez względu
   * na „Moje / Zespół” — awaria dotyczy wszystkich) + inne „Pilne” z Inboxu, które nie są alarmem incydentu (reguła
   * Fali 3: element Inbox niezrobiony i nieodłożony). `incidents` = sama liczba incydentów, `firstIncidentId` =
   * najnowszy incydent (kafelek z jednym alarmem prowadzi do widoku incydentu), `firstTitle` = „rea.pl nie odpowiada”.
   */
  alarms: {
    open: number;
    firstTitle: string | null;
    incidents: number;
    firstIncidentId: string | null;
    /** pierwszy alarm pochodzi z symulowanego monitora (kafelek z plakietką „Symulacja”) */
    firstSimulated: boolean;
  };
  /**
   * Niezafakturowany czas (moduł Czas albo Rozliczenia; bez obu: null, UI pokazuje „—”): wpisy rozliczane, które
   * nie są w żadnym zestawieniu — ta sama definicja co kafelek „Niezafakturowane” w Rozliczeniach (Figma 09)
   * i „Do rozliczenia” w profilu klienta (`billing.unbilledTotals`) dla osoby z modułem Rozliczenia (Zespół = klienci
   * widoczni w całości); bez modułu Rozliczenia — definicja Czasu (`time.unbilledSummary`: cały zakres wpisów osoby,
   * także klienci widoczni tylko przez projekt; Przegląd Fali 11). Moje = moje wpisy, Zespół = cały zakres osoby. `amount` tylko z „Widzi stawki i kwoty”, inaczej null; kwota jest w walucie
   * `currency`, kwoty w innych walutach w `otherAmounts` (waluty się nie sumuje).
   */
  unbilled: {
    amount: string | null;
    currency?: string;
    otherAmounts?: CurrencyAmount[];
    seconds: number;
    entries: number;
  } | null;
  /**
   * Do wypłaty (Przegląd Fali 11): moje nierozliczone wpisy ze stawką partnera (wszystkie okresy, bez zamkniętych
   * rozliczeń) — tylko dla osoby bez modułu Rozliczenia, która ma stawkę partnera (domyślną, w projekcie albo na
   * wpisie). Kafelek „Do wypłaty” zastępuje wtedy „Niezafakturowane” (fakturowanie klienta to sprawa prowadzącego).
   * Kwota jest zawsze widoczna osobie wpisu (DECISIONS 2026-10-08 [P] „Widoczność kwot”). Brak pola = kafelek
   * „Niezafakturowane”.
   */
  partnerPayout?: {
    seconds: number;
    entries: number;
    amount: string;
    currency: string;
    /** najstarszy nierozliczony wpis (YYYY-MM-DD, Europe/Warsaw); null = nic nie czeka */
    since: string | null;
  };
  /**
   * Najbliższe spotkanie (moduł Klienci; Moje = moje spotkania: autor albo uczestnik). null = brak albo bez modułu.
   */
  nextMeeting: { id: string; startsAt: string; title: string; clientName: string | null } | null;
  needsYou: TodayNeedsYouItem[];
  plan: TodayPlanItem[];
}

/** Akceptacje czekające na decyzję (np. baner w profilu klienta). */
export const pendingApprovalsInput = z
  .object({
    clientId: idSchema.optional(),
    /** tylko akceptacje, które osoba może zatwierdzić */
    decidableOnly: z.boolean().default(false),
    limit: z.number().int().min(1).max(50).default(5),
  })
  .default({});
export type PendingApprovalsInput = z.input<typeof pendingApprovalsInput>;

export interface PendingApprovalItem {
  id: string;
  type: string;
  summary: string;
  clientId: string | null;
  initiatorType: string;
  createdAt: Date;
  /** osoba może zatwierdzić (flaga z typu akcji) */
  canDecide: boolean;
  /** rekord, którego dotyczy akcja (np. `billing_draft`, `meeting`); null = bez rekordu */
  target: { type: string; id: string } | null;
}

/**
 * Rekord, na którym akcję widać (zestawienie, spotkanie z propozycjami zadań, oferta), inaczej moduł Akceptacje
 * (`/akceptacje?id=`). Od Fali 3 „Wymaga Ciebie” i baner profilu klienta prowadzą do modułu Akceptacje
 * (`approvalCardPath`, `clientApprovalsPath`); ta funkcja daje `reviewPath` karty („Przejrzyj w spotkaniu”).
 */
export function approvalPath(a: { id: string; target: { type: string; id: string } | null }): string {
  switch (a.target?.type) {
    case 'billing_draft':
      return `/rozliczenia/${a.target.id}`;
    case 'meeting':
      return `/spotkania/${a.target.id}`;
    case 'offer':
      return `/oferty/${a.target.id}`;
    default:
      return `/akceptacje?id=${a.id}`;
  }
}

export interface PendingApprovals {
  total: number;
  /** wszystkie czekające zainicjowało AI (baner „akcja AI czeka…”) */
  allByAi: boolean;
  oldestWaitingSec: number | null;
  items: PendingApprovalItem[];
}

// ── Dziś: widoki Tydzień i Miesiąc (DECISIONS 2026-10-09 „Dziś: Dzień / Tydzień / Miesiąc”) ──────────────────

/** Najdłuższy zakres `today.calendar` (dni włącznie): miesiąc z zapasem na pełne tygodnie siatki. */
export const TODAY_CALENDAR_MAX_DAYS = 42;

export const TODAY_CALENDAR_MESSAGES = {
  order: 'Data „do” nie może być wcześniejsza niż „od”',
  span: `Zakres może mieć najwyżej ${TODAY_CALENDAR_MAX_DAYS} dni`,
} as const;

/**
 * `today.calendar({ scope, from, to })` — rzeczy rozłożone na dni (Europe/Warsaw, oba końce włącznie): spotkania
 * (kalendarz iCal i moduł Spotkania), otwarte zadania z terminem, odnowienia i płatności z Utrzymania, terminy
 * projektów oraz wpisany czas dzień po dniu. „Moje / Zespół” jak w `today.summary`.
 */
export const todayCalendarInput = z
  .object({ scope: todayScopeSchema.default('mine'), from: localDateSchema, to: localDateSchema })
  .superRefine((v, ctx) => {
    if (v.to < v.from) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: TODAY_CALENDAR_MESSAGES.order });
    } else if (diffDaysLocal(v.from, v.to) + 1 > TODAY_CALENDAR_MAX_DAYS) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: TODAY_CALENDAR_MESSAGES.span });
    }
  });
export type TodayCalendarInput = z.input<typeof todayCalendarInput>;

/**
 * Rodzaj pozycji kalendarza: spotkanie (niebieski znacznik), zadanie (szary), odnowienie / płatność z Utrzymania
 * (bursztyn), termin projektu (szary). Po terminie (czerwony) to `overdue: true` przy zadaniu albo odnowieniu.
 */
export const TODAY_CALENDAR_KINDS = ['meeting', 'task', 'renewal', 'project_deadline'] as const;
export type TodayCalendarKind = (typeof TODAY_CALENDAR_KINDS)[number];

export interface TodayCalendarItem {
  /** `<kind>:<id rekordu>` (odnowienie: `renewal:<service|supplier_renewal>:<id>`) */
  id: string;
  kind: TodayCalendarKind;
  /**
   * Dzień w kalendarzu (YYYY-MM-DD). Zaległe zadania i odnowienia z terminem PRZED zakresem trafiają na dziś, gdy
   * dziś jest w zakresie (praca po terminie „ląduje” na dziś); z terminem w zakresie zostają na swoim dniu.
   */
  date: string;
  /** godzina początku spotkania „HH:MM” (Europe/Warsaw); inne rodzaje: null */
  time: string | null;
  endTime: string | null;
  title: string;
  /** klient (albo projekt / projekt wewnętrzny) */
  context: string | null;
  /** termin rekordu (zadanie, odnowienie, projekt); spotkanie: null */
  dueDate: string | null;
  overdue: boolean;
  /** kolor znacznika: accent = spotkanie, warning = odnowienie, danger = po terminie, neutral = zadanie / projekt */
  tone: StatusTone;
  /** ścieżka SPA rekordu */
  to: string;
}

export interface TodayCalendar {
  scope: TodayScope;
  from: string;
  to: string;
  /** dziś (Europe/Warsaw) wg serwera */
  today: string;
  /** kolejność: dzień, potem po terminie → spotkania (wg godziny) → odnowienia → terminy projektów → zadania */
  items: TodayCalendarItem[];
  /** wpisany czas dzień po dniu (każdy dzień zakresu); null = bez modułu Czas */
  days: Array<{ date: string; seconds: number }> | null;
  /**
   * Niezafakturowany czas z wpisów zakresu (rozliczane, bez zestawienia; Moje = moje wpisy). Kwota tylko z „Widzi
   * stawki i kwoty” i tylko klientów widocznych w całości; null = bez modułu Czas.
   */
  unbilled: { seconds: number; amount?: string; currency?: string; otherAmounts?: CurrencyAmount[] } | null;
}

const CALENDAR_KIND_RANK: Record<TodayCalendarItem['kind'], number> = {
  meeting: 1,
  renewal: 2,
  project_deadline: 3,
  task: 4,
};

/**
 * Kolejność pozycji `items` (serwer i SPA): dzień, potem po terminie → spotkania (wg godziny) → odnowienia → terminy
 * projektów → zadania. Pierwsze 3 pozycje dnia to kafelki w widoku Tydzień.
 */
export function compareCalendarItems(a: TodayCalendarItem, b: TodayCalendarItem): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  const ra = a.overdue ? 0 : CALENDAR_KIND_RANK[a.kind];
  const rb = b.overdue ? 0 : CALENDAR_KIND_RANK[b.kind];
  if (ra !== rb) return ra - rb;
  if ((a.time ?? '') !== (b.time ?? '')) return (a.time ?? '') < (b.time ?? '') ? -1 : 1;
  if ((a.dueDate ?? '') !== (b.dueDate ?? '')) return (a.dueDate ?? '') < (b.dueDate ?? '') ? -1 : 1;
  return a.title.localeCompare(b.title, 'pl');
}
